import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactSpeedometer from 'react-d3-speedometer';
import { subscribeToMicrophoneSamples } from '@shared/DSPHandler/services/MicrophoneService';
import { DEFAULT_VOICE_LOUDNESS_CONFIG, VOICE_LOUDNESS_LABEL_MODES } from '../../types';
import {
  calculateNoiseCompensatedRmsDb,
  RANGE_MAX_DB,
  RANGE_MIN_DB,
} from './audioLevel';
import { VOICE_LOUDNESS_COLORS } from './colors';
import { SERIAL_RANGE_MAX } from './serialScale';
import {
  Adjustment,
  Gauge,
  Surface,
} from './styles';

const SEGMENT_COUNT = 60;
const LABEL_COUNT = SERIAL_RANGE_MAX;
const VALUE_UPDATE_MS = 100;
const SPEECH_GAP_HOLD_MS = 300;
const ATTACK_SMOOTHING = 0.6;
const RELEASE_SMOOTHING = 0.2;

const getColor = (value, targetLow, targetHigh) => {
  if (value >= targetLow && value <= targetHigh) return VOICE_LOUDNESS_COLORS.TARGET;
  if (value > targetHigh) return VOICE_LOUDNESS_COLORS.TOO_LOUD;
  return VOICE_LOUDNESS_COLORS.TOO_LOW;
};

const buildSegments = (targetLow, targetHigh, labelMode) => {
  const step = (RANGE_MAX_DB - RANGE_MIN_DB) / SEGMENT_COUNT;
  const slicesPerLabel = SEGMENT_COUNT / LABEL_COUNT;
  const stops = [RANGE_MIN_DB];
  const colors = [];
  const labels = [];

  for (let index = 0; index < SEGMENT_COUNT; index += 1) {
    const lower = RANGE_MIN_DB + index * step;
    const upper = RANGE_MIN_DB + (index + 1) * step;
    stops.push(upper);
    colors.push(getColor((lower + upper) / 2, targetLow, targetHigh));

    const isLabeledSlice = (index + 1) % slicesPerLabel === 0;
    let text = '';
    if (isLabeledSlice) {
      text = labelMode === VOICE_LOUDNESS_LABEL_MODES.EXACT
        ? `${Math.round(upper)}`
        : `${(index + 1) / slicesPerLabel}`;
    }
    labels.push({
      text,
      position: 'OUTSIDE',
      fontSize: '11px',
      color: VOICE_LOUDNESS_COLORS.TEXT,
    });
  }

  return { stops, colors, labels };
};

const VoiceLoudness = ({ config: configuredValues, onConfigChange }) => {
  const { t } = useTranslation();
  const config = { ...DEFAULT_VOICE_LOUDNESS_CONFIG, ...configuredValues };
  const [level, setLevel] = useState(RANGE_MIN_DB);
  const lastUpdateRef = useRef(0);
  const lastSpeechAtRef = useRef(0);
  const displayedLevelRef = useRef(RANGE_MIN_DB);
  const pendingSamplesRef = useRef([]);

  useEffect(() => subscribeToMicrophoneSamples((samples) => {
    for (let index = 0; index < samples.length; index += 1) {
      pendingSamplesRef.current.push(samples[index]);
    }

    const now = Date.now();
    if (now - lastUpdateRef.current < VALUE_UPDATE_MS) return;
    lastUpdateRef.current = now;

    const measuredLevel = calculateNoiseCompensatedRmsDb(pendingSamplesRef.current);
    pendingSamplesRef.current = [];
    if (measuredLevel > RANGE_MIN_DB) {
      lastSpeechAtRef.current = now;
    } else if (now - lastSpeechAtRef.current < SPEECH_GAP_HOLD_MS) {
      return;
    }

    const smoothing = measuredLevel > displayedLevelRef.current
      ? ATTACK_SMOOTHING
      : RELEASE_SMOOTHING;
    displayedLevelRef.current += smoothing * (measuredLevel - displayedLevelRef.current);
    setLevel(displayedLevelRef.current);
  }), []);

  const shiftTarget = (delta) => {
    const clampedDelta = Math.max(
      RANGE_MIN_DB - config.targetLowDbfs,
      Math.min(RANGE_MAX_DB - config.targetHighDbfs, delta),
    );
    onConfigChange({
      ...config,
      targetLowDbfs: config.targetLowDbfs + clampedDelta,
      targetHighDbfs: config.targetHighDbfs + clampedDelta,
    });
  };

  const segments = buildSegments(config.targetLowDbfs, config.targetHighDbfs, config.labelMode);

  return (
    <Surface aria-label={t('UI.voiceLoudness')}>
      <Gauge>
        <ReactSpeedometer
          key={`${config.targetLowDbfs}_${config.targetHighDbfs}_${config.labelMode}`}
          width={360}
          height={210}
          minValue={RANGE_MIN_DB}
          maxValue={RANGE_MAX_DB}
          value={level}
          segments={SEGMENT_COUNT}
          customSegmentStops={segments.stops}
          segmentColors={segments.colors}
          customSegmentLabels={segments.labels}
          needleColor={VOICE_LOUDNESS_COLORS.NEEDLE}
          needleTransitionDuration={150}
          ringWidth={30}
          textColor={VOICE_LOUDNESS_COLORS.TEXT}
          valueTextFontSize="14px"
          currentValueText=" "
        />
      </Gauge>
      <Adjustment
        position="top"
        onClick={() => shiftTarget(config.trimStepDb)}
        aria-label={t('UI.voiceLoudnessTooEasy')}
      >
        {t('UI.voiceLoudnessTooEasy')}
      </Adjustment>
      <Adjustment
        position="bottom"
        onClick={() => shiftTarget(-config.trimStepDb)}
        aria-label={t('UI.voiceLoudnessTooHard')}
      >
        {t('UI.voiceLoudnessTooHard')}
      </Adjustment>
    </Surface>
  );
};

VoiceLoudness.propTypes = {
  config: PropTypes.object,
  onConfigChange: PropTypes.func.isRequired,
};

export default VoiceLoudness;